import {observer} from "mobx-react-lite";
import React, {useEffect, useMemo, useState} from "react";
import {
  AppDialog,
  AppLightTooltip,
  AppSectionErrorBoundary,
  AppSpacer,
  AppUseIcon
} from "../../../components/ui";
import { t } from 'utilities/TranslationUtils';
import {AppButton, AppInput} from "../../../components/form";
import { IOrganization } from "types/models/OrganizationModels";
import { ShouldNotBeEmpty } from "utilities/Validations";
import { EmptyGuid } from "utilities/Constants";
import { ValidationType } from "types/GlobalTypes";
import { useStores } from "index";

export const AddUpdateOrganizationDialog = observer(({
    selectedOrganization,
    onClose,
    onSubmit,
    isOpened,
  }: {
    selectedOrganization: IOrganization;
    onClose: () => void;
    onSubmit: (organization: IOrganization) => void;
    isOpened: boolean;
  }) => {
    const [id, setId] = useState(selectedOrganization.id);
    const [name, setName] = useState(selectedOrganization.name);
    const [shortName, setShortName] = useState(selectedOrganization.shortName);
    const [externalId, setExternalId] = useState(selectedOrganization.externalId);

    const { organizationsStore } = useStores();

    useEffect(() => {
      setId(selectedOrganization.id);
      setName(selectedOrganization.name);
      setShortName(selectedOrganization.shortName);
      setExternalId(selectedOrganization.externalId);
    }, [selectedOrganization]);

    const generateIssuesMessage = (): string => {
      if(IsShortNameInvalid(shortName) || IsNameInvalid(name)) {
        return t('organizationsPage.validationIssues');
      }

      return '';
    };

    const onNameChange = (value: string) => {
      setName(value);
    }
    const IsNameInvalid: ValidationType = (value: string) => organizationsStore.IsOrganizationNameInvalid(value, id);

    const onShortNameChange = (value: string) => {
      setShortName(value);
    }
    const IsShortNameInvalid: ValidationType = (value: string) => organizationsStore.IsOrganizationShortNameInvalid(value, id);

    const onExternalIdChange = (value: string) => {
      setExternalId(value);
    }

    const onSubmitHandler = () => {
      const updatedOrganization: IOrganization = {
        id: id,
        name: name,
        shortName: shortName,
        externalId: externalId
      };
      clearInputs();
      onSubmit(updatedOrganization);
    };

    const onCloseHandler = () => {
      clearInputs();
      onClose();
    }

    const clearInputs = () => {
      setId(EmptyGuid);
      setName('');
      setShortName('');
      setExternalId('');
    }

    const getDialogTitle = () => {
      if (id === EmptyGuid) { 
        return t('organizationsPage.addOrganization');
      }
      return t('organizationsPage.updateOrganization');
    };

    return (
      <div>
        <AppDialog
          title={getDialogTitle()}
          onClose={onClose}
          isOpened={isOpened}
        >
          <AppSectionErrorBoundary>
            <div className='container'>
              { id != EmptyGuid ? (
                <>
                  {t('organizationsPage.organizationId')}: {id != EmptyGuid ? id : ''}
                  <AppSpacer space='10px' />
                </>
              ) : null}
              <AppInput
                label={`* ${t('organizationsPage.name')}`}
                hint={t('organizationsPage.nameHint')}
                name='organizationName'
                value={name}
                type='email'
                validationRules={[IsNameInvalid]}
                onChange={(e) => onNameChange(e.target.value)}
                disabled={false}
                data-test='add-organization-name-input'
              />
              <AppSpacer space='10px' />
              <AppInput
                label={`* ${t('organizationsPage.shortName')}`}
                hint={t('organizationsPage.shortNameHint')}
                name='organizationShortName'
                value={shortName}
                type='text'
                validationRules={[IsShortNameInvalid]}
                onChange={(e) => onShortNameChange(e.target.value)}
                disabled={false}
                data-test='add-organization-short-name-input'
              />
              <AppSpacer space='10px' />
              <AppInput
                label={`${t('organizationsPage.externalId')}`}
                hint={t('organizationsPage.externalIdHint')}
                name='organizationExternalId'
                value={externalId}
                type='text'
                onChange={(e) => onExternalIdChange(e.target.value)}
                disabled={false}
                data-test='add-organization-external-id-input'
              />
            </div>
            <footer className='d-flex  justify-content-between align-items-center p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
              <div className='d-flex justify-content-end w-100'>
                {generateIssuesMessage() ? (
                  <div className='d-flex align-items-center'>
                    <AppLightTooltip title={generateIssuesMessage()} className='d-flex align-items-center'>
                      <AppUseIcon name='InfoIcon' width='16' height='16' className='ml-3' color='#ee1b34' />
                    </AppLightTooltip>
                  </div>
                ) : null}
                <AppSpacer space='5px'></AppSpacer>
                <AppButton variant='secondary' onClick={onCloseHandler} data-test='dialog-close-button'>
                  {t('common.close')}
                </AppButton>
                <AppSpacer space='5px'></AppSpacer>
                <AppButton
                  variant='primary' onClick={onSubmitHandler}
                  disabled={generateIssuesMessage() != ''}>
                  {t(id === EmptyGuid ? 'common.add' : 'common.update')}
                </AppButton>
              </div>
            </footer>
          </AppSectionErrorBoundary>
        </AppDialog>
      </div>
    )
})